import React from 'react';
import cn from 'classnames';

import { Typography } from 'components/shared/Typography';
import { Collapsible } from 'components/shared/Collapsible';
import { Icon } from 'components/shared/Icon';
import { Checkbox } from 'components/shared/Checkbox';

import { getTranslation } from 'helpers/getTranslation';

import { TextTextProperty } from '../TextTextProperty';
import { getIconNameByValue } from '../CategoryCheckbox/CategoryCheckbox.helpers';

import {
  getFlowStartingPointsCheckValues,
  getMetaCategoryCheckValue,
  getPrototypeStartNodeCheckValues,
} from './PagePrototypeMeta.helpers';

import styles from './PagePrototypeMeta.module.scss';

type FlowStartingPoint = {
  name: string;
  nodeName: string | null;
  checks?: {
    name?: boolean;
    nodeName?: boolean;
  };
};

type PrototypeStartNode = {
  name: string;
  type: string;
  checks?: {
    name?: boolean;
    type?: boolean;
  };
} | null;

const PagePrototypeMeta = ({
  isInitialState,
  readOnly,
  designType,
  taskStatePrefix,
  flowStartingPoints,
  prototypeStartNode,
  onFlowCategoryCheckChanged,
  onStartNodeCategoryCheckChanged,
}: {
  isInitialState: boolean;
  readOnly: boolean;
  designType: string;
  taskStatePrefix: string;
  flowStartingPoints: FlowStartingPoint[];
  prototypeStartNode: PrototypeStartNode;
  onFlowCategoryCheckChanged: (checked: boolean) => void;
  onStartNodeCategoryCheckChanged: (checked: boolean) => void;
}) => {
  const checkable = !isInitialState;
  const hasFlowStartingPoints = flowStartingPoints.length > 0;
  const hasPrototypeStartNode = Boolean(prototypeStartNode);
  const flowCategoryCheckValue = getMetaCategoryCheckValue(
    getFlowStartingPointsCheckValues(flowStartingPoints),
  );
  const startNodeCategoryCheckValue = getMetaCategoryCheckValue(
    getPrototypeStartNodeCheckValues(prototypeStartNode),
  );

  if (!hasFlowStartingPoints && !hasPrototypeStartNode) {
    return null;
  }

  return (
    <>
      {hasFlowStartingPoints && (
        <Collapsible
          transitionTime={150}
          triggerTagName="div"
          className={styles.section}
          openedClassName={cn(styles.section, styles.sectionOpened)}
          open
          trigger={({ onTriggerClick }: { onTriggerClick: () => void }) => (
            <div className={styles.header}>
              {checkable && (
                <Checkbox
                  className={styles.check}
                  checked={
                    flowCategoryCheckValue === 'checked' ||
                    flowCategoryCheckValue === 'partiallyChecked'
                  }
                  icon={getIconNameByValue(flowCategoryCheckValue)}
                  onChange={(event) => {
                    event.stopPropagation();
                    onFlowCategoryCheckChanged(event.target.checked);
                  }}
                />
              )}
              <div className={styles.headerInner} onClick={onTriggerClick}>
                <Typography
                  className={styles.title}
                  variant="body2"
                  weight="medium"
                  mode="compact"
                >
                  {getTranslation(
                    'figma_lesson_page.main_settings.prototype_section.flow_starting_points_title',
                  )}
                </Typography>
                <Icon className={styles.icon} name="chevronDown" />
              </div>
            </div>
          )}
        >
          <div className={styles.inner}>
            {flowStartingPoints.map((point, index) => (
              <div className={styles.propertiesRow} key={`${point.name}-${index}`}>
                <TextTextProperty
                  checkable={checkable}
                  isInitialState={isInitialState}
                  readOnly={readOnly}
                  designType={designType}
                  namePrefix={`${taskStatePrefix}.flowStartingPoints[${index}].checks.name`}
                  property={{
                    key: 'flowStartingPointName',
                    value: point.name,
                  }}
                />
                <TextTextProperty
                  checkable={checkable}
                  isInitialState={isInitialState}
                  readOnly={readOnly}
                  designType={designType}
                  namePrefix={`${taskStatePrefix}.flowStartingPoints[${index}].checks.nodeName`}
                  property={{
                    key: 'flowStartingPointNodeName',
                    value:
                      point.nodeName ??
                      getTranslation(
                        'figma_lesson_page.main_settings.prototype_section.missing_layer',
                      ),
                  }}
                />
              </div>
            ))}
          </div>
        </Collapsible>
      )}

      {hasPrototypeStartNode && prototypeStartNode && (
        <Collapsible
          transitionTime={150}
          triggerTagName="div"
          className={styles.section}
          openedClassName={cn(styles.section, styles.sectionOpened)}
          open
          trigger={({ onTriggerClick }: { onTriggerClick: () => void }) => (
            <div className={styles.header}>
              {checkable && (
                <Checkbox
                  className={styles.check}
                  checked={
                    startNodeCategoryCheckValue === 'checked' ||
                    startNodeCategoryCheckValue === 'partiallyChecked'
                  }
                  icon={getIconNameByValue(startNodeCategoryCheckValue)}
                  onChange={(event) => {
                    event.stopPropagation();
                    onStartNodeCategoryCheckChanged(event.target.checked);
                  }}
                />
              )}
              <div className={styles.headerInner} onClick={onTriggerClick}>
                <Typography
                  className={styles.title}
                  variant="body2"
                  weight="medium"
                  mode="compact"
                >
                  {getTranslation(
                    'figma_lesson_page.main_settings.prototype_section.prototype_start_node_title',
                  )}
                </Typography>
                <Icon className={styles.icon} name="chevronDown" />
              </div>
            </div>
          )}
        >
          <div className={styles.inner}>
            <div className={styles.propertiesRow}>
              <TextTextProperty
                checkable={checkable}
                isInitialState={isInitialState}
                readOnly={readOnly}
                designType={designType}
                namePrefix={`${taskStatePrefix}.prototypeStartNode.checks.name`}
                property={{
                  key: 'prototypeStartNodeName',
                  value: prototypeStartNode.name,
                }}
              />
              <TextTextProperty
                checkable={checkable}
                isInitialState={isInitialState}
                readOnly={readOnly}
                designType={designType}
                namePrefix={`${taskStatePrefix}.prototypeStartNode.checks.type`}
                property={{
                  key: 'prototypeStartNodeType',
                  value: prototypeStartNode.type,
                }}
              />
            </div>
          </div>
        </Collapsible>
      )}
    </>
  );
};

export default React.memo(PagePrototypeMeta) as any;
